import React, { useState, useEffect, useRef } from 'react';
import { ProjectionResponse, MonthProjection } from '../types/Projection';
import { getProjections } from '../services/api';
import AskExplain from './AskExplain';
import './Projections.css';
import { brlOrMasked } from '../utils/format';

interface ProjectionsProps {
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  refreshTrigger?: number;
  selectedMonth?: string | null;
  onMonthSelect?: (monthKey: string, monthLabel: string) => void;
  /** Item 17: grava a âncora de saldo. Não recebe mais o saldo antigo — ele não é usado. */
  onBalanceAdjust?: (newBalance: number) => Promise<void>;
}

type VisaoDaTabela = 'mes' | 'acumulado';
const CHAVE_DA_VISAO = 'fintracking.projecao.visao';

const Projections: React.FC<ProjectionsProps> = ({ refreshTrigger, selectedMonth, onMonthSelect, onBalanceAdjust, hideValues }) => {
  const [projections, setProjections] = useState<ProjectionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [months, setMonths] = useState(6);
  const [pastMonths, setPastMonths] = useState(0);

  /*
   * No celular as nove colunas não cabem, então a tabela tem duas VISÕES, e
   * cada uma cabe na tela sem rolar de lado:
   *
   *   - `mes`:       o que entrou e saiu naquele mês
   *   - `acumulado`: quanto ela tem ao fim dele
   *
   * São as duas perguntas que a tabela responde, e por isso o corte é esse e
   * não "as colunas que sobraram". No desktop (e com o celular deitado) o CSS
   * mostra tudo e este estado não faz nada.
   *
   * Guardado por aparelho. É só preferência de exibição — nenhum número
   * depende disso.
   */
  const [visao, setVisao] = useState<VisaoDaTabela>(() => {
    try {
      return localStorage.getItem(CHAVE_DA_VISAO) === 'mes' ? 'mes' : 'acumulado';
    } catch { return 'acumulado'; }
  });
  const escolherVisao = (v: VisaoDaTabela) => {
    setVisao(v);
    try { localStorage.setItem(CHAVE_DA_VISAO, v); } catch { /* sem storage, vale só nesta visita */ }
  };

  const [editingBalance, setEditingBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const balanceInputRef = useRef<HTMLInputElement>(null);

  /*
   * Para que pergunta os dados na tela respondem (15/09).
   *
   * Revalidar (a aba voltou, um lançamento foi gravado) trocava a seção
   * inteira por "Carregando projeções...": a página encolhia, pulava, e ela
   * via a tela "atualizando sozinha". Com os dados do MESMO horizonte na
   * tela, eles ficam até os novos chegarem. Horizonte diferente mostra o
   * carregando — número de outro período na tela, nem por um instante.
   */
  const [chaveCarregada, setChaveCarregada] = useState<string | null>(null);
  const chave = `${months}:${pastMonths}`;

  const loadProjections = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProjections(months, pastMonths);
      setProjections(data);
      setChaveCarregada(`${months}:${pastMonths}`);
    } catch (err) {
      console.error('Erro ao carregar projeções:', err);
      /*
       * "Verifique se o backend está rodando" é instrução para quem
       * desenvolve, não para quem usa. Ela não tem backend para verificar, e a
       * frase transforma uma falha do app numa tarefa dela — com um verbo no
       * imperativo sobre uma coisa que ela não controla.
       *
       * O que serve é dizer o que aconteceu e oferecer o caminho, que o botão
       * logo abaixo já é.
       */
      setError('Não conseguimos carregar os dados agora.');
      setProjections(null);
      setChaveCarregada(null);
    } finally {
      setLoading(false);
    }
  }, [months, pastMonths]);

  useEffect(() => {
    loadProjections();
  }, [months, pastMonths, refreshTrigger, loadProjections]);

  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const formatCurrency = (value: number) => brlOrMasked(value, !!hideValues);

  /*
   * O valor numa célula da tabela, com o "R$" num <span> à parte.
   *
   * O número continua vindo de `formatCurrency` — aqui só se separa o símbolo
   * para o CSS poder escondê-lo no celular, onde quatro colunas de "R$ 10.200,00"
   * não cabem em 320px e o cabeçalho da coluna já diz que é dinheiro. Texto que
   * não começa com "R$" (o valor oculto pelo olhinho) passa intacto.
   */
  const celula = (value: number, prefixo = ''): React.ReactNode => {
    const texto = formatCurrency(value);
    const m = /^(-?)R\$\s?(.+)$/.exec(texto);
    if (!m) return <span className="dinheiro">{prefixo}{texto}</span>;
    /*
     * O valor inteiro numa caixa que não quebra (05/10).
     *
     * Hífen é ponto de quebra de linha para o navegador: numa coluna apertada
     * o "-" ficava sozinho em cima e "R$ 2.750,88" embaixo, e um saldo
     * negativo lia como positivo com um traço solto. O `&nbsp;` já segurava
     * "R$" junto do número; faltava segurar o sinal.
     */
    return <span className="dinheiro">{prefixo}{m[1]}<span className="moeda">R$&nbsp;</span>{m[2]}</span>;
  };

  const formatCurrencyInput = (raw: string): string => {
    const isNegative = raw.startsWith('-');
    const digits = raw.replace(/\D/g, '');
    if (!digits) return isNegative ? '-' : '';
    const cents = parseInt(digits, 10);
    const formatted = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return isNegative ? `-${formatted}` : formatted;
  };

  const parseCurrencyInput = (masked: string): number => {
    const isNegative = masked.startsWith('-');
    const value = parseFloat(masked.replace(/\./g, '').replace(',', '.')) || 0;
    return isNegative ? -Math.abs(value) : value;
  };

  const openBalanceEdit = () => {
    if (!projections || !onBalanceAdjust) return;
    const current = projections.currentCashBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    setBalanceInput(current);
    setEditingBalance(true);
    setTimeout(() => balanceInputRef.current?.select(), 50);
  };

  const cancelBalanceEdit = () => {
    setEditingBalance(false);
    setBalanceInput('');
  };

  const confirmBalanceAdjust = async () => {
    if (!projections || !onBalanceAdjust) return;
    const newBalance = parseCurrencyInput(balanceInput);
    if (newBalance === projections.currentCashBalance) { cancelBalanceEdit(); return; }
    setAdjusting(true);
    try {
      await onBalanceAdjust(newBalance);
      setEditingBalance(false);
      setBalanceInput('');
    } finally {
      setAdjusting(false);
    }
  };

  if (loading && !(projections && chaveCarregada === chave)) {
    return (
      <div className="projections-container">
        <div className="loading-wrap">
          <div className="loading-ring" />
          <span className="loading-text">Carregando projeções...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="projections-container">
        <div className="empty-state">
          <p>{error}</p>
          <button type="button" className="btn-retry" onClick={loadProjections}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!projections || !projections.projections || projections.projections.length === 0) {
    return (
      <div className="projections-container">
        <div className="empty-state">
          <p>Não há dados suficientes para gerar projeções.</p>
        </div>
      </div>
    );
  }

  const vs = projections.variableSpend;
  const negativeLabel = projections.projections
    .find(p => p.month === projections.cashGoesNegativeAt)?.monthLabel ?? projections.cashGoesNegativeAt;

  const futureProjections = projections.projections.filter(p => !p.isPast);
  const totalIncome   = futureProjections.reduce((s, p) => s + p.income, 0);
  const totalExpense  = futureProjections.reduce((s, p) => s + p.expenses, 0);
  const finalPatrimony = futureProjections.length > 0
    ? (futureProjections[futureProjections.length - 1].patrimony ?? 0)
    : (projections.patrimony?.total ?? 0);
  const ultimo = futureProjections[futureProjections.length - 1];
  const finalCash = ultimo ? ultimo.cumulativeCashReal : projections.currentCashBalance;
  const finalCashEstimado = ultimo ? ultimo.cumulativeCashBalance : projections.currentCashBalance;
  /**
   * Real e estimado em colunas separadas (03/11): "Saldo do mês" e "Em caixa"
   * só com o que está lançado; "Saldo estimado" e "Caixa estimado" descontam o
   * gasto variável que ainda deve vir. Sem histórico para estimar, as colunas
   * estimadas repetiriam as reais, e somem.
   */
  const temEstimativa = vs.monthlyEstimate !== null;

  /**
   * O rendimento da carteira entra no PATRIMÔNIO e não no CAIXA — render não
   * deposita nada na conta corrente. A coluna só aparece quando há alguma
   * aplicação com taxa cadastrada; sem isso seria uma coluna de traços.
   */
  const carteira = projections.investments;
  const temRendimento = projections.projections.some(p => p.investmentEarnings != null);

  return (
    <div className="projections-container">
      <div className="projections-header">
        <h2>Projeção Financeira</h2>
        <div className="projections-controls">
          <div className="past-months-selector">
            <span className="past-months-label">Histórico:</span>
            {[0, 3, 6, 12].map(p => (
              <button
                key={p}
                className={`months-btn${pastMonths === p ? ' active' : ''}`}
                onClick={() => setPastMonths(p)}
              >
                {p === 0 ? 'Off' : `${p}m`}
              </button>
            ))}
          </div>
          <div className="months-selector">
            {[6, 12, 18, 24].map(m => (
              <button
                key={m}
                className={`months-btn${months === m ? ' active' : ''}`}
                onClick={() => setMonths(m)}
              >
                {m}m
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* A manchete e o CAIXA, nao o patrimonio. Era o inverso, e o patrimonio
          virava a nota de rodape "em caixa: X" — dois numeros disputando o mesmo
          rotulo. Sob o modelo dela, saldo (disponivel) + investido (aplicacoes e
          reservas) ja da o total, entao patrimonio e derivavel e nao precisa de
          lugar proprio. */}
      <div className={`current-balance ${projections.currentCashBalance >= 0 ? 'positive' : 'negative'}`}>
        <span className="current-balance-label">Em caixa</span>
        {editingBalance ? (
          <div className="balance-edit-wrap">
            <input
              ref={balanceInputRef}
              className="balance-edit-input"
              type="text"
              inputMode="numeric"
              value={balanceInput}
              onChange={e => setBalanceInput(formatCurrencyInput(e.target.value))}
              onKeyDown={e => { if (e.key === 'Enter') confirmBalanceAdjust(); if (e.key === 'Escape') cancelBalanceEdit(); }}
              disabled={adjusting}
            />
            <button className="balance-edit-confirm" onClick={confirmBalanceAdjust} disabled={adjusting} title="Confirmar ajuste">
              {adjusting ? <span className="balance-edit-spinner" /> : '✓'}
            </button>
            <button className="balance-edit-cancel" onClick={cancelBalanceEdit} disabled={adjusting} title="Cancelar">✕</button>
          </div>
        ) : (
          <div className="balance-display-wrap">
            <span className="balance-amount">{formatCurrency(projections.currentCashBalance)}</span>
            {/* Caixa e patrimônio lado a lado, nomeados. São coisas diferentes:
                caixa é o que dá para gastar hoje, patrimônio é o que foi
                guardado e saiu do caixa justamente por isso. Um número só
                apagaria a diferença que faz a pessoa decidir. */}
            {!!projections.patrimony?.total && (
              <span className="balance-patrimonio" title="Reservas de metas + investimentos. Não inclui o caixa.">
                patrimônio: {formatCurrency(projections.patrimony.total)}
              </span>
            )}
            {onBalanceAdjust && (
              <button className="balance-edit-btn" onClick={openBalanceEdit} title="Ajustar saldo">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Item 15 — a UI precisa ser honesta sobre de onde vem o número. */}
      {vs.monthlyEstimate === null ? (
        <div className="proj-note proj-note-warn">
          <strong>Sem estimativa de gasto variável.</strong> São necessários ao menos 3 meses
          com lançamentos para projetar. Os meses futuros mostram apenas contas fixas e
          parcelas, e o saldo aparece mais otimista do que a realidade.
        </div>
      ) : (
        <div className="proj-note">
          Gasto variável estimado em <strong>{formatCurrency(vs.monthlyEstimate)}/mês</strong>,
          mediana de {vs.monthsUsed.length} {vs.monthsUsed.length === 1 ? 'mês' : 'meses'} com lançamento
          {vs.staleMonths >= 2 && (
            <>{' '}(<span className="proj-note-stale">o último foi há {vs.staleMonths} meses</span>)</>
          )}. Em cada mês, nunca passa do que sobra.
        </div>
      )}

      {temRendimento && carteira && (
        <div className="proj-note">
          Sua carteira de <strong>{formatCurrency(carteira.total)}</strong> deve render{' '}
          <strong>{formatCurrency(carteira.projectedEarnings)}</strong> no período.
          Isso entra no <strong>saldo acumulado</strong> (patrimônio) e não no{' '}
          <strong>em caixa</strong>, porque render não põe dinheiro na conta corrente.
          {carteira.withRate < carteira.count && (
            <> {carteira.count - carteira.withRate} de {carteira.count} aplicações estão sem
            taxa cadastrada e não rendem nada nesta conta.</>
          )}
        </div>
      )}

      {projections.cashGoesNegativeAt && (
        <div className="proj-note proj-note-alert">
          Contando o gasto estimado, seu caixa fica negativo em <strong>{negativeLabel}</strong>.
        </div>
      )}

      {/* Só no celular em pé: o CSS esconde acima de 768px, onde a tabela já
          mostra todas as colunas. */}
      <div className="proj-visao" role="group" aria-label="O que a tabela mostra">
        <button
          type="button"
          className={`months-btn${visao === 'mes' ? ' active' : ''}`}
          aria-pressed={visao === 'mes'}
          onClick={() => escolherVisao('mes')}
        >
          Do mês
        </button>
        <button
          type="button"
          className={`months-btn${visao === 'acumulado' ? ' active' : ''}`}
          aria-pressed={visao === 'acumulado'}
          onClick={() => escolherVisao('acumulado')}
        >
          Acumulado
        </button>
      </div>

      <div className={`projections-table-container visao-${visao}`}>
        <table className={`projections-table${temEstimativa ? ' com-estimativa' : ''}`}>
          <thead>
            {/* O `\u00ad` é hífen OPCIONAL: só aparece se a coluna for mais
                estreita que a palavra (celular em pé). É o que deixa o título
                inteiro caber sem abreviar. */}
            <tr>
              <th>Mês</th>
              <th className="g-mes">{'Recebi\u00admentos'}</th>
              <th className="g-mes">Gastos</th>
              <th className="g-mes" title="Só o que está lançado">Saldo do Mês</th>
              {temEstimativa && (
                <>
                  <th className="g-mes" title="Gasto variável que ainda deve vir, pelo seu histórico, descontado o que já está lançado">Estimado</th>
                  <th className="g-mes" title="Saldo do mês menos o gasto estimado">{'Saldo Esti\u00admado'}</th>
                </>
              )}
              {temRendimento && (
                <th className="g-acum" title="Rendimento projetado da sua carteira: soma no patrimônio, não no caixa">
                  {'Rendi\u00admento'}
                </th>
              )}
              {/* Três totais, e cada um responde uma pergunta diferente.
                  A coluna que saiu ("Saldo Acumulado") era caixa MAIS o que
                  saiu do caixa para reserva — um híbrido que não respondia
                  nenhuma delas e fazia o futuro parecer mais folgado. */}
              <th className="g-acum" title="O que dá para gastar, só com o que está lançado">Em Caixa</th>
              {temEstimativa && (
                <th className="g-acum" title="Em caixa menos o gasto estimado acumulado">{'Caixa Esti\u00admado'}</th>
              )}
              <th className="g-acum" title="Reservas de metas + investimentos. Não inclui o caixa.">{'Patri\u00admônio'}</th>
              <th className="g-acum" title={temEstimativa ? 'Caixa estimado + patrimônio' : 'Em caixa + patrimônio'}>Total</th>
            </tr>
          </thead>
          <tbody>
            {projections.projections.map((projection: MonthProjection, idx: number) => {
              const isSelected = selectedMonth === projection.month;
              const isPast = projection.isPast;
              const isLastPast = isPast && !projections.projections[idx + 1]?.isPast;
              return (
                <tr
                  key={projection.month}
                  className={`projection-row${isSelected ? ' selected' : ''}${onMonthSelect ? ' clickable' : ''}${isPast ? ' past-row' : ''}${isLastPast ? ' past-row-last' : ''}${projection.month === projections.cashGoesNegativeAt ? ' cash-negative-row' : ''}`}
                  onClick={() => onMonthSelect?.(projection.month, projection.monthLabel)}
                  title={onMonthSelect ? `Ver transações de ${projection.monthLabel}` : undefined}
                >
                  {/*
                    O flex vive num <span> DENTRO da célula, e não na célula.
                    
                    `.month-cell` tinha `display: flex`, e um `<td>` com flex
                    deixa de ser célula de tabela: sai do layout da linha, e a
                    borda de baixo dele para de participar do
                    `border-collapse` com as vizinhas. O efeito era o traço
                    separador da primeira coluna desalinhado do resto da linha
                    — a "linha torta".
                  */}
                  <td className="month-cell">
                    <span className="month-cell-conteudo">
                      {isPast && <span className="past-badge">hist.</span>}
                      {projection.monthLabel}
                      {isSelected && <span className="selected-indicator">●</span>}
                    </span>
                  </td>
                  <td className="g-mes income-cell">{celula(projection.income)}</td>
                  <td className="g-mes expense-cell">{celula(projection.expenses)}</td>
                  <td className={`g-mes balance-cell ${projection.realBalance >= 0 ? 'positive' : 'negative'}`}>
                    {celula(projection.realBalance)}
                  </td>
                  {temEstimativa && (
                    <>
                      <td className="g-mes estimate-cell">
                        {projection.estimatedExpenses !== null
                          ? celula(projection.estimatedExpenses)
                          : <span className="estimate-none" title="Mês com lançamentos reais">—</span>}
                      </td>
                      <td className={`g-mes estimate-cell${projection.estimatedExpenses !== null ? ` ${projection.balance >= 0 ? 'positive' : 'negative'}` : ''}`}>
                        {projection.estimatedExpenses !== null
                          ? celula(projection.balance)
                          : <span className="estimate-none" title="Mês com lançamentos reais">—</span>}
                      </td>
                    </>
                  )}
                  {temRendimento && (
                    <td className="g-acum earnings-cell">
                      {projection.investmentEarnings != null
                        ? celula(projection.investmentEarnings, '+')
                        : <span className="estimate-none" title="Mês com saldo real cadastrado, nada é simulado aqui">—</span>}
                    </td>
                  )}
                  <td className={`g-acum cash-cell${isPast ? ' past-cumulative' : ` ${projection.cumulativeCashReal >= 0 ? 'positive' : 'negative'}`}`}>
                    {isPast ? '—' : celula(projection.cumulativeCashReal)}
                  </td>
                  {temEstimativa && (
                    <td className={`g-acum estimate-cell${isPast ? ' past-cumulative' : ` ${projection.cumulativeCashBalance >= 0 ? 'positive' : 'negative'}`}`}>
                      {isPast ? '—' : celula(projection.cumulativeCashBalance)}
                    </td>
                  )}
                  <td className={`g-acum patrimony-cell${isPast ? ' past-cumulative' : ''}`}>
                    {isPast || projection.patrimony == null ? '—' : celula(projection.patrimony)}
                  </td>
                  <td className={`g-acum cumulative-cell${isPast ? ' past-cumulative' : ` ${(projection.cumulativeCashBalance + (projection.patrimony ?? 0)) >= 0 ? 'positive' : 'negative'}`}`}>
                    {/* Derivado, não armazenado: duas versões do total seriam
                        duas oportunidades de divergir. */}
                    {isPast ? '—' : celula(projection.cumulativeCashBalance + (projection.patrimony ?? 0))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="proj-dica-girar">Vire o celular para ver todas as colunas de uma vez.</p>

      {/* A tela que mais pede confiança e menos explicava. */}
      <AskExplain scope="projection" hideValues={hideValues} />

      <div className="projections-summary">
        <div className="summary-item">
          <span className="summary-label">Total Recebimentos</span>
          <span className="summary-value income">{formatCurrency(totalIncome)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Gastos</span>
          <span className="summary-value expense">{formatCurrency(totalExpense)}</span>
        </div>
        {/* Mesmo vocabulário da tabela: caixa e patrimônio, nunca o híbrido. */}
        <div className="summary-item">
          <span className="summary-label">Em Caixa ao Final</span>
          <span className={`summary-value ${finalCash >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(finalCash)}
          </span>
        </div>
        {temEstimativa && (
          <div className="summary-item">
            <span className="summary-label">Caixa Estimado ao Final</span>
            <span className={`summary-value ${finalCashEstimado >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(finalCashEstimado)}
            </span>
          </div>
        )}
        {finalPatrimony > 0 && (
          <div className="summary-item">
            <span className="summary-label">Patrimônio ao Final</span>
            <span className="summary-value positive">
              {formatCurrency(finalPatrimony)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projections;
